import React, { useRef, useEffect } from 'react';

function NCube({ dimension = 4, size = 150, strokeColor = "#222222", strokeWidth = 1 }) {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const rotationRef = useRef({ xy: 0, xz: 0, xw: 0, yz: 0, yw: 0, zw: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size for retina displays with padding
    const scale = window.devicePixelRatio || 1;
    const canvasSize = size * 2; // 2x size
    canvas.width = canvasSize * scale;
    canvas.height = canvasSize * scale;
    canvas.style.width = canvasSize + 'px';
    canvas.style.height = canvasSize + 'px';
    ctx.scale(scale, scale);

    // Generate vertices for n-dimensional hypercube
    const generateVertices = (n) => {
      const vertices = [];
      const numVertices = Math.pow(2, n);
      
      for (let i = 0; i < numVertices; i++) {
        const vertex = [];
        for (let j = 0; j < n; j++) {
          vertex.push((i >> j) & 1 ? 1 : -1);
        }
        vertices.push(vertex);
      }
      return vertices;
    };

    // Generate edges
    const generateEdges = (n) => {
      const edges = [];
      const numVertices = Math.pow(2, n);
      
      for (let i = 0; i < numVertices; i++) {
        for (let j = i + 1; j < numVertices; j++) {
          let diff = 0;
          for (let k = 0; k < n; k++) {
            if (((i >> k) & 1) !== ((j >> k) & 1)) diff++;
          }
          if (diff === 1) edges.push([i, j]);
        }
      }
      return edges;
    };

    // Rotation matrix for n dimensions
    const rotate = (vertices, rotations) => {
      const rotated = vertices.map(v => [...v]);
      
      // Apply rotations in different planes
      if (dimension >= 2 && rotations.xy) {
        const cos = Math.cos(rotations.xy);
        const sin = Math.sin(rotations.xy);
        rotated.forEach(v => {
          const x = v[0] * cos - v[1] * sin;
          const y = v[0] * sin + v[1] * cos;
          v[0] = x;
          v[1] = y;
        });
      }
      
      if (dimension >= 3 && rotations.xz) {
        const cos = Math.cos(rotations.xz);
        const sin = Math.sin(rotations.xz);
        rotated.forEach(v => {
          const x = v[0] * cos - v[2] * sin;
          const z = v[0] * sin + v[2] * cos;
          v[0] = x;
          v[2] = z;
        });
      }
      
      // Apply rotations for all dimension pairs
      for (let d1 = 0; d1 < dimension - 1; d1++) {
        for (let d2 = d1 + 1; d2 < dimension; d2++) {
          const rotKey = `d${d1}d${d2}`;
          if (rotations[rotKey]) {
            const cos = Math.cos(rotations[rotKey]);
            const sin = Math.sin(rotations[rotKey]);
            rotated.forEach(v => {
              const coord1 = v[d1] * cos - v[d2] * sin;
              const coord2 = v[d1] * sin + v[d2] * cos;
              v[d1] = coord1;
              v[d2] = coord2;
            });
          }
        }
      }
      
      return rotated;
    };

    // Project to 2D
    const project = (vertices, canvasSize) => {
      // Reduce scale for higher dimensions to fit in view
      const scaleFactor = dimension <= 4 ? 0.35 : 0.15 / Math.log(dimension);
      const scale = size * scaleFactor;
      const distance = 3;
      
      return vertices.map(vertex => {
        let x = vertex[0] || 0;
        let y = vertex[1] || 0;
        
        // Apply perspective projection from higher dimensions down to 2D
        for (let d = dimension - 1; d >= 2; d--) {
          const coord = vertex[d] || 0;
          const factor = distance / (distance - coord * 0.5);
          x *= factor;
          y *= factor;
        }
        
        return {
          x: x * scale + canvasSize / 2,
          y: y * scale + canvasSize / 2
        };
      });
    };

    const vertices = generateVertices(dimension);
    const edges = generateEdges(dimension);

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      
      // Update rotations for all dimension pairs
      for (let d1 = 0; d1 < dimension - 1; d1++) {
        for (let d2 = d1 + 1; d2 < dimension; d2++) {
          const rotKey = `d${d1}d${d2}`;
          if (!rotationRef.current[rotKey]) rotationRef.current[rotKey] = 0;
          // Vary rotation speeds for visual interest
          rotationRef.current[rotKey] += 0.001 + (d1 + d2) * 0.0005;
        }
      }
      
      // Rotate and project vertices
      const rotatedVertices = rotate(vertices, rotationRef.current);
      const projectedVertices = project(rotatedVertices, canvasSize);
      
      // Draw edges
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth;
      ctx.globalAlpha = 0.8;
      
      edges.forEach(([i, j]) => {
        ctx.beginPath();
        ctx.moveTo(projectedVertices[i].x, projectedVertices[i].y);
        ctx.lineTo(projectedVertices[j].x, projectedVertices[j].y);
        ctx.stroke();
      });
      
      // Draw vertices
      ctx.fillStyle = strokeColor;
      projectedVertices.forEach(vertex => {
        ctx.beginPath();
        ctx.arc(vertex.x, vertex.y, 2, 0, Math.PI * 2);
        ctx.fill();
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimension, size, strokeColor, strokeWidth]);

  return (
    <canvas 
      ref={canvasRef}
      className="w-full h-full"
      style={{ maxWidth: size * 2 + 'px', maxHeight: size * 2 + 'px' }}
    />
  );
}

export default NCube;